import Card from '@/components/layout/Card';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoUpload from '@/components/Formulario/CampoUpload';
import Marcadores from '@/components/layout/Marcadores';
import Modal from '@/components/layout/Modal';
import { mdiPlus, mdiTagPlus, mdiTrashCan } from '@mdi/js';
import { ClienteAnexoDto } from '@tera/shared/src/dto/ClienteDto';
import { useMutation, useQuery } from 'react-query';
import { queryClient } from '@/queryClient';
import axios from 'axios';
import { toast } from 'react-toastify';
import React from 'react';

type Props = {
  clienteId: number | undefined;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const ArquivosCliente = ({ clienteId }: Props) => {
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [novoArquivo, setNovoArquivo] = React.useState<File | null>(null);
  const [novosMarcadoresUpload, setNovosMarcadoresUpload] = React.useState<
    { id: number; nome: string; cor: string }[]
  >([]);
  const [novoMarcadorUploadId, setNovoMarcadorUploadId] = React.useState('');

  const qArquivos = useQuery<ClienteAnexoDto[]>(
    ['cliente_anexo', clienteId],
    async () => {
      const response = await axios.get(`/api/cliente/${clienteId}/anexo`);
      return response.data.result;
    },
    { enabled: !!clienteId },
  );

  const uploadArquivoMutation = useMutation({
    mutationFn: async ({
      file,
      marcadores,
    }: {
      file: File;
      marcadores: { id: number }[];
    }) => {
      const formData = new FormData();
      formData.append('arquivo', file);
      const response = await axios.post(
        `/api/cliente/${clienteId}/anexo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (marcadores.length) {
        await axios.patch(
          `/api/cliente/${clienteId}/anexo/${response.data.result.id}/marcadores`,
          { marcadores: marcadores.map((m) => ({ id: m.id })) },
        );
      }
    },
    onSuccess: () => {
      setShowUploadModal(false);
      setNovoArquivo(null);
      setNovosMarcadoresUpload([]);
      setNovoMarcadorUploadId('');
      queryClient.invalidateQueries(['cliente_anexo', clienteId]);
    },
    onError: () => {
      toast('Erro ao enviar arquivo', {
        toastId: 'error',
        position: 'top-center',
        type: 'error',
        autoClose: 5000,
      });
    },
  });

  const qTags = useQuery('tags', async () => {
    const { data } = await axios.get('/api/marcador/box');
    return data?.result ?? [];
  });

  const optionsTagsArquivo = qTags.data
    ?.filter((tag: any) => ['qualquer', 'anexo'].includes(tag.local))
    ?.map((tag: any) => ({ id: tag.id, label: tag.nome }));

  const [tagAnexoAlvo, setTagAnexoAlvo] = React.useState<ClienteAnexoDto | null>(
    null,
  );
  const [selectedTagAnexoId, setSelectedTagAnexoId] = React.useState('');

  const atualizarMarcadoresArquivoMutation = useMutation({
    mutationFn: async ({
      anexoId,
      marcadores,
    }: {
      anexoId: number;
      marcadores: { id: number }[];
    }) => {
      await axios.patch(
        `/api/cliente/${clienteId}/anexo/${anexoId}/marcadores`,
        { marcadores: marcadores.map((m) => ({ id: m.id })) },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cliente_anexo', clienteId]);
      setTagAnexoAlvo(null);
      setSelectedTagAnexoId('');
    },
    onError: () => {
      toast('Erro ao atualizar marcadores do arquivo', {
        toastId: 'error',
        position: 'top-center',
        type: 'error',
        autoClose: 5000,
      });
    },
  });

  const excluirArquivoMutation = useMutation({
    mutationFn: async (anexoId: number) => {
      await axios.delete(`/api/cliente/${clienteId}/anexo/${anexoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cliente_anexo', clienteId]);
    },
    onError: () => {
      toast('Erro ao excluir arquivo', {
        toastId: 'error',
        position: 'top-center',
        type: 'error',
        autoClose: 5000,
      });
    },
  });

  const handleDownloadArquivo = async (anexo: ClienteAnexoDto) => {
    try {
      const response = await axios.get(
        `/api/cliente/${clienteId}/anexo/${anexo.id}/download`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', anexo.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast('Erro ao baixar arquivo', {
        toastId: 'error',
        position: 'top-center',
        type: 'error',
        autoClose: 5000,
      });
    }
  };

  return (
    <div>
      <Card>
        <HBox stretch>
          <p style={{ textAlign: 'center', fontWeight: 600 }}>Arquivos</p>
          <Botao
            variant="none-primary"
            icon={mdiPlus}
            onClick={() => setShowUploadModal(true)}
          ></Botao>
        </HBox>

        {qArquivos.data?.length ? (
          qArquivos.data.map((anexo) => {
            const idsMarcadoresAnexo = anexo.marcadores?.map((m) => m.id) ?? [];

            return (
              <VBox
                key={anexo.id}
                style={{
                  borderBottom: '1px solid var(--tc-color-gray-100)',
                  padding: '4px 0',
                }}
              >
                <HBox stretch>
                  <span
                    style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={`${anexo.filename} (${formatFileSize(anexo.tamanho)})`}
                    onClick={() => handleDownloadArquivo(anexo)}
                  >
                    {anexo.filename}
                  </span>
                  <BotaoPerigoso
                    variant="none-danger"
                    icon={mdiTrashCan}
                    confirmation={<span>Tem certeza que deseja excluir este arquivo?</span>}
                    onClick={() => excluirArquivoMutation.mutate(anexo.id)}
                  ></BotaoPerigoso>
                </HBox>

                <HBox stretch style={{ alignItems: 'center' }}>
                  <Marcadores
                    ids={idsMarcadoresAnexo}
                    onRemove={(id) =>
                      atualizarMarcadoresArquivoMutation.mutate({
                        anexoId: anexo.id,
                        marcadores: (anexo.marcadores ?? []).filter(
                          (m) => m.id !== id,
                        ),
                      })
                    }
                  />
                  <Botao
                    noArrow
                    variant="none-primary"
                    icon={mdiTagPlus}
                    title="Adicionar marcador"
                    onClick={() => {
                      setSelectedTagAnexoId('');
                      setTagAnexoAlvo(anexo);
                    }}
                  />
                </HBox>
              </VBox>
            );
          })
        ) : (
          <span style={{ color: 'var(--tc-color-gray-500)' }}>
            Nenhum arquivo adicionado
          </span>
        )}
      </Card>

      <Modal
        show={showUploadModal}
        title="Adicionar Arquivo"
        size="md"
        onClose={() => {
          setShowUploadModal(false);
          setNovoArquivo(null);
          setNovosMarcadoresUpload([]);
          setNovoMarcadorUploadId('');
        }}
        onCancel={() => {
          setShowUploadModal(false);
          setNovoArquivo(null);
          setNovosMarcadoresUpload([]);
          setNovoMarcadorUploadId('');
        }}
        onConfirm={() => {
          if (novoArquivo) {
            uploadArquivoMutation.mutate({
              file: novoArquivo,
              marcadores: novosMarcadoresUpload,
            });
          } else {
            toast('Selecione um arquivo', { toastId: 'warning', position: 'top-center', type: 'warning', autoClose: 5000 });
          }
        }}
        confirmProps={{ disabled: uploadArquivoMutation.isLoading }}
      >
        <VBox>
          <CampoUpload
            instanceId="upload-arquivo-cliente"
            label="Selecione um arquivo"
            onChange={(files) => setNovoArquivo(files[0])}
            value={novoArquivo ? [novoArquivo] : []}
            maxFiles={1}
            accept="*/*"
          />

          <label>Marcadores (opcional)</label>
          <HBox stretch>
            <CampoSeletor
              instanceId="marcadores-upload-arquivo"
              options={
                optionsTagsArquivo?.filter(
                  (it: any) =>
                    !novosMarcadoresUpload.some((m) => m.id === it.id),
                ) ?? []
              }
              value={novoMarcadorUploadId}
              onChange={(v) => setNovoMarcadorUploadId((v as string) ?? '')}
              placeholder="Selecione um marcador"
              noSearch
            />
            <Botao
              icon={mdiPlus}
              variant="outline-primary"
              onClick={() => {
                const tag = qTags.data?.find(
                  (t: any) => t.id === +novoMarcadorUploadId,
                );
                if (!tag) return;

                setNovosMarcadoresUpload((s) => [...s, tag]);
                setNovoMarcadorUploadId('');
              }}
            />
          </HBox>
          <Marcadores
            ids={novosMarcadoresUpload.map((m) => m.id)}
            onRemove={(id) =>
              setNovosMarcadoresUpload((s) => s.filter((m) => m.id !== id))
            }
          />
        </VBox>
      </Modal>

      <Modal
        show={!!tagAnexoAlvo}
        onClose={() => setTagAnexoAlvo(null)}
        onCancel={() => setTagAnexoAlvo(null)}
        onConfirm={() => {
          if (!tagAnexoAlvo || !selectedTagAnexoId) return;
          atualizarMarcadoresArquivoMutation.mutate({
            anexoId: tagAnexoAlvo.id,
            marcadores: [
              ...(tagAnexoAlvo.marcadores ?? []),
              { id: +selectedTagAnexoId },
            ],
          });
        }}
        title="Adicionar marcador ao arquivo"
        confirmProps={{
          label: 'Adicionar',
          disabled: atualizarMarcadoresArquivoMutation.isLoading,
        }}
      >
        <VBox>
          <CampoSeletor
            options={
              optionsTagsArquivo?.filter(
                (it: any) =>
                  !tagAnexoAlvo?.marcadores?.some((m) => m.id === it.id),
              ) ?? []
            }
            value={selectedTagAnexoId}
            onChange={(v) => setSelectedTagAnexoId((v as string) ?? '')}
            placeholder="Selecione um marcador"
          />
        </VBox>
      </Modal>
    </div>
  );
};

export default ArquivosCliente;
